import { MenuItem, Select, styled, Typography } from '@mui/material';
import { TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { FeedbackContext } from '../../../constants/feedbackConstants';
import { processQuestion, userEditSave } from '../../../services/dealGPT';
import { type ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { contentGenerationAnalysisPages, ProcessReason, UpdatedReason } from '../../../Types/enums';
import { FeedbackInterface } from '../../../Types/feedback';
import { formatDate } from '../../../util/dateUtils';
import { renderMessage } from '../../../util/messageUtils';
import Feedback from '../../Feedback';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useFeatures } from '../../Providers/FeatureProvider';
import { useNotifications } from '../../Providers/NotificationsProvider';
import { AugmentedResponseWithSources } from '../SourcesDisplay';
import ModifyTone from './ModifyTone';
import ReadMoreText from './ReadMoreText';
import ReferenceDocuments from './ReferenceDocuments';
import SectionButtons from './SectionButtons';
import UnprocessedChangesDot from './UnprocessedChangesDot';

interface PromptAnswerProps {
  projectId: string;
  versions: ProjectRunAnalysisQuestion[];
  textfieldName: string;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  refetchQuestions: () => void;
  timeToProcess: string;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  onSelectVersion?: (versionId: string) => void;
}

const ReactMarkdownWrapper = styled('div')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
});

const PromptAnswer: React.FC<PromptAnswerProps> = ({
  projectId,
  versions,
  textfieldName,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  refetchQuestions,
  timeToProcess,
  handleFeedbackSubmit,
  onSelectVersion,
}) => {
  const [analysisQuestionData, setAnalysisQuestionData] = useState<ProjectRunAnalysisQuestion>(
    versions[0]
  );
  const [questionVersions, setQuestionVersions] = useState<ProjectRunAnalysisQuestion[]>(versions);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const analytics = useAnalytics();
  const { refetchNotifications } = useNotifications();

  // This is used when the user clicks the save button
  const handleSave = async (
    values: Record<string, string>,
    reason?: UpdatedReason,
    processReason?: ProcessReason,
    overwriteQuestionData?: Partial<ProjectRunAnalysisQuestion>
  ) => {
    const tempAnalysisQuestion: ProjectRunAnalysisQuestion = { ...analysisQuestionData };
    tempAnalysisQuestion.response = values[textfieldName];

    if (reason) {
      tempAnalysisQuestion.updatedReason = reason;
    }

    if (overwriteQuestionData) {
      Object.assign(tempAnalysisQuestion, overwriteQuestionData);
    }

    let updatedQuestion = tempAnalysisQuestion;
    if (reason === UpdatedReason.USER_EDIT) {
      try {
        const updatedResult = await userEditSave(tempAnalysisQuestion);
        updatedQuestion = updatedResult;
        setAnalysisQuestionData(updatedResult);
        enqueueSnackbar('Content saved successfully', { variant: 'success' });
        refetchQuestions();
      } catch {
        enqueueSnackbar('Failed to save content. Please try again later.', {
          variant: 'error',
        });
      }
    }

    if (
      tempAnalysisQuestion.updatedReason === UpdatedReason.REPROCESS ||
      processReason === ProcessReason.APPLY_CHANGES
    ) {
      try {
        enqueueSnackbar('Your content is being processed', { variant: 'info' });
        setIsLoadingThis(true);
        if (processReason === ProcessReason.APPLY_CHANGES) {
          setIsLoadingApplyChanges(true);
        }
        await processQuestion(updatedQuestion, processReason, Boolean(overwriteQuestionData));
        refetchNotifications();
        refetchQuestions();
      } catch {
        enqueueSnackbar('Failed to apply changes. Please try again later.', {
          variant: 'error',
        });
      } finally {
        setIsLoadingThis(false);
        setIsLoadingApplyChanges(false);
      }
    }
  };

  const handleVersionChange = useCallback(
    async (value: string) => {
      setSelectedVersion(value);
      analytics.event(
        AnalyticsEvent.WM_IA_A_PA_QUESTION_VERSION_CHANGE,
        {
          id: value,
          value: versions[0].pageName,
        },
        {
          pageName: versions[0].pageName,
          oldVersionId: analysisQuestionData.id,
          newVersionId: value,
          scope: versions[0].scope,
          runId: versions[0].runId,
          projectId,
        }
      );
      if (onSelectVersion) {
        onSelectVersion(value);
      }
    },
    [versions, analytics, projectId, analysisQuestionData.id, onSelectVersion]
  );

  const onSubmit = async () => {
    // setIsLoading(true);
    // enqueueSnackbar('Your content is being processed', { variant: 'success' });
    // const tempAnalysisQuestion = await handleSave(
    //   analysisQuestionData as unknown as Record<string, string>,
    //   UpdatedReason.REPROCESS
    // );
    // if (tempAnalysisQuestion) {
    //   await processQuestion(tempAnalysisQuestion, false);
    //   refetchQuestions();
    // }
  };

  const handleRefetchQuestions = () => {
    setSelectedVersion(null);
    refetchQuestions();
  };

  useEffect(() => {
    if (versions.length > 0) {
      setQuestionVersions(versions);
      if (selectedVersion) {
        const version = versions.find((v) => v.id === selectedVersion);
        if (version) {
          setAnalysisQuestionData(version);
          return;
        }
      }
      setAnalysisQuestionData(versions[0]);
    }
  }, [versions, selectedVersion]);

  const features = useFeatures();

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ [textfieldName]: analysisQuestionData?.response || '' }}
      render={({ handleSubmit, values }) => {
        if (!analysisQuestionData) {
          return (
            <Typography
              sx={{ fontWeight: '400', color: '#070154', fontSize: '16px', textAlign: 'center' }}
            >
              No data is available to display yet. Please check back later.
            </Typography>
          );
        }

        return (
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                paddingBottom: '24px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography sx={{ fontWeight: '700', color: '#092B49' }}>
                  {analysisQuestionData.promptType}
                </Typography>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Select
                    value={
                      questionVersions.some((v) => v.id === analysisQuestionData?.id)
                        ? analysisQuestionData.id
                        : questionVersions[0].id
                    }
                    onChange={(event) => handleVersionChange(event.target.value)}
                    size="small"
                  >
                    {questionVersions.map((version) => (
                      <MenuItem
                        value={version.id}
                        key={version.id}
                        selected={version.id === analysisQuestionData.id}
                      >
                        Generated on {version.createdAt && formatDate(version.createdAt)} |{' '}
                        {version.updatedReason}
                      </MenuItem>
                    ))}
                  </Select>
                  {questionVersions[0].unprocessedDownstreamChanges && <UnprocessedChangesDot />}
                </div>
              </div>
              <ModifyTone
                projectId={projectId}
                question={analysisQuestionData}
                setIsLoading={setIsLoadingThis}
                refetchQuestions={handleRefetchQuestions}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <SectionButtons
                  projectId={projectId}
                  handleSave={handleSave}
                  values={values}
                  hasGenerated={true}
                  handleEdit={setIsEditing}
                  isEditing={isEditing}
                  question={analysisQuestionData}
                  timeToProcess={timeToProcess}
                  allowReprompt={Object.values(contentGenerationAnalysisPages).includes(
                    analysisQuestionData.pageName as contentGenerationAnalysisPages
                  )}
                />
                {isEditing ? (
                  <TextField name={textfieldName} multiline />
                ) : (
                  <ReactMarkdownWrapper>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, supersub]}
                      components={{
                        sup: ({ children }) => (
                          <AugmentedResponseWithSources
                            text={children.toString()}
                            sources={analysisQuestionData.documents ?? []}
                          />
                        ),
                      }}
                    >
                      {renderMessage({
                        message: analysisQuestionData.response || '',
                        sources: analysisQuestionData.documents || [],
                      })}
                    </ReactMarkdown>
                  </ReactMarkdownWrapper>
                )}
              </div>

              <ReferenceDocuments
                questionId={analysisQuestionData.id}
                documents={analysisQuestionData.documents}
                showAnalysisText={true}
              />
              {features.deal_gpt_enriched_prompt_display && (
                <>
                  <Typography sx={{ fontWeight: '700', color: '#092B49', marginTop: '20px' }}>
                    Enriched Prompt:
                  </Typography>
                  <ReadMoreText text={analysisQuestionData.enrichedPrompt || ''} maxLength={120} />
                </>
              )}
              <Feedback
                initialFeedback={
                  analysisQuestionData.analysisQuestionFeedback as FeedbackInterface | undefined
                }
                context={FeedbackContext.ANALYSIS_QUESTION}
                onFeedbackSubmit={(feedback) =>
                  handleFeedbackSubmit(feedback, analysisQuestionData.id)
                }
              />
            </div>
          </form>
        );
      }}
    />
  );
};

export default PromptAnswer;
